import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface ContextVariable {
  name: string;
  type: string;
  description: string;
  nullable: boolean;
}

export interface ContextSchema {
  type: string;
  description: string;
  variables: ContextVariable[];
}

export async function getTwigContextType(
  scope: RequestScope,
  args: { type: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ContextSchema>> {
  if (!args.type || !args.type.trim()) {
    return {
      isError: true,
      failureKind: 'failed',
      status: 0,
      errors: { error: 'type is required' },
    };
  }

  const type = args.type.trim();

  try {
    const res = await scopedChannelApi(scope).get<ContextSchema>(`twig/context-types/${encodeURIComponent(type)}`, {
      ...options,
      headers: buildAsturHeaders(),
    });

    const variables: ContextVariable[] = Array.isArray(res.data?.variables)
      ? res.data.variables.map((v) => ({
          name: typeof v?.name === 'string' ? v.name : '',
          type: typeof v?.type === 'string' ? v.type : '',
          description: typeof v?.description === 'string' ? v.description : '',
          nullable: Boolean(v?.nullable),
        }))
      : [];

    // Per-field defaulting is intentional: if the backend returns 200 with a partial shape,
    // surface what we can rather than failing the whole call. Callers needing strict validation
    // should check `variables.length` and `description` against their own expectations.
    return {
      data: {
        type: typeof res.data?.type === 'string' ? res.data.type : type,
        description: typeof res.data?.description === 'string' ? res.data.description : '',
        variables,
      },
      status: res.status,
    };
  } catch (e) {
    // Schema fetches don't have user-authored payloads, but the request
    // surface still carries Authorization headers we shouldn't ship to
    // Bugsnag. Use the same redact-on-error policy as the other twig endpoints.
    return utils.error.handleAxiosError(e, { redactRequestData: true });
  }
}
